import axios from "axios";
import { t } from "i18next";
import { toast } from "sonner";

import EncoderAPI from "../../../api/EncoderAPI";
import MediaAPI from "../../../api/MediaAPI";

import type { QueryClient, UseMutationOptions } from "@tanstack/react-query";

import type {
  DeleteSourceMediaErrorResponse,
  GetSourceResponse,
  GetStreamsResponse,
  StartPivotEncodingResponse,
} from "../../../api/EncoderAPI.types";

interface StreamMutationOptions {
  mediaId: string;
  queryClient: QueryClient;
}

interface DeleteSourceMediaVariables {
  sourceMediaId: string;
  filename: string;
}

interface DeleteSourceMediaContext {
  previousSources: GetSourceResponse | undefined;
}

export function deleteSourceMediaMutation({
  mediaId,
  queryClient,
}: StreamMutationOptions): UseMutationOptions<
  unknown,
  unknown,
  DeleteSourceMediaVariables,
  DeleteSourceMediaContext
> {
  const getSourceQueryKey = EncoderAPI.generateQuery({
    fn: EncoderAPI.getSource,
    params: [mediaId],
  }).queryKey;

  return {
    mutationFn: async ({ sourceMediaId, filename }) =>
      EncoderAPI.deleteSourceMedia({ mediaId, filename, sourceMediaId }),

    onMutate: async ({ sourceMediaId }) => {
      await queryClient.cancelQueries(getSourceQueryKey);
      const previousSources =
        queryClient.getQueryData<GetSourceResponse>(getSourceQueryKey);

      if (!previousSources) {
        return { previousSources };
      }

      if (previousSources.type === "source_media") {
        queryClient.setQueryData<GetSourceResponse>(getSourceQueryKey, {
          ...previousSources,
          data: previousSources.data.filter(
            (sourceMedia) => sourceMedia.uuid !== sourceMediaId,
          ),
        });
      } else {
        const deletedPivot = previousSources.data.find(
          (pivotMedia) => pivotMedia.uuid === sourceMediaId,
        );

        // The deleted item is either a pivot (in `data`, whose underlying
        // source media is referenced via `source_media_uuid`) or a standalone
        // source media such as a WebVTT file (directly in `source_medias`,
        // identified by its own uuid).
        const deletedSourceMediaId = deletedPivot
          ? deletedPivot.source_media_uuid
          : sourceMediaId;

        queryClient.setQueryData<GetSourceResponse>(getSourceQueryKey, {
          ...previousSources,
          data: previousSources.data.filter(
            (pivotMedia) => pivotMedia.uuid !== sourceMediaId,
          ),
          source_medias: previousSources.source_medias.filter(
            (sourceMedia) => sourceMedia.uuid !== deletedSourceMediaId,
          ),
        });
      }

      return { previousSources };
    },

    onSuccess: () => {
      toast.success(t("editor-notif:deleteSourceMediaSuccess"));
    },

    onError: (error, _variables, context) => {
      const errorData = axios.isAxiosError(error)
        ? (error.response?.data as DeleteSourceMediaErrorResponse | undefined)
        : undefined;
      if (errorData?.error === "stream_are_still_used_by_hosted_streams") {
        toast.error(t("editor-notif:deleteSourceMediaUsedByStreamError"), {
          duration: 3000,
        });
      } else {
        toast.error(t("editor-notif:deleteSourceMediaError"), {
          duration: 3000,
        });
      }
      queryClient.setQueryData(getSourceQueryKey, context?.previousSources);
    },
  };
}

interface DeleteStreamVariables {
  streamId: string;
}

interface DeleteStreamContext {
  previousStreams: GetStreamsResponse | undefined;
}

export function deleteStreamMutation({
  mediaId,
  queryClient,
}: StreamMutationOptions): UseMutationOptions<
  unknown,
  unknown,
  DeleteStreamVariables,
  DeleteStreamContext
> {
  const getStreamsQueryKey = EncoderAPI.generateQuery({
    fn: EncoderAPI.getStreams,
    params: [mediaId],
  }).queryKey;

  return {
    mutationFn: async ({ streamId }: DeleteStreamVariables) =>
      EncoderAPI.deleteStream(streamId),

    onMutate: async ({
      streamId,
    }: DeleteStreamVariables): Promise<DeleteStreamContext> => {
      await queryClient.cancelQueries(getStreamsQueryKey);
      const previousStreams =
        queryClient.getQueryData<GetStreamsResponse>(getStreamsQueryKey);
      queryClient.setQueryData(getStreamsQueryKey, {
        data:
          previousStreams?.data.filter((stream) => stream.uuid !== streamId) ??
          [],
      });
      return { previousStreams };
    },

    onSuccess: (): void => {
      toast.success(t("editor-notif:deleteStreamSuccess"));
    },

    onError: (
      _error: unknown,
      _variables: DeleteStreamVariables,
      context: DeleteStreamContext | undefined,
    ): void => {
      toast.error(t("editor-notif:deleteStreamError"), {
        duration: 3000,
      });
      queryClient.setQueryData(getStreamsQueryKey, context?.previousStreams);
    },
  };
}

interface UpdateStreamVariables {
  streamId: string;
  name?: string;
  language_uuid?: string;
  type?: "subtitles" | "chapters";
}

interface UpdateStreamContext {
  previousStreams: GetStreamsResponse | undefined;
}

export function updateStreamMutation({
  mediaId,
  queryClient,
}: StreamMutationOptions): UseMutationOptions<
  unknown,
  unknown,
  UpdateStreamVariables,
  UpdateStreamContext
> {
  const getStreamsQueryKey = EncoderAPI.generateQuery({
    fn: EncoderAPI.getStreams,
    params: [mediaId],
  }).queryKey;

  return {
    mutationFn: async ({
      streamId,
      name,
      language_uuid,
      type,
    }: UpdateStreamVariables) =>
      EncoderAPI.updateStream(streamId, { name, language_uuid, type }),

    onMutate: async ({
      streamId,
      name,
      language_uuid,
      type,
    }: UpdateStreamVariables): Promise<UpdateStreamContext> => {
      await queryClient.cancelQueries(getStreamsQueryKey);
      const previousStreams =
        queryClient.getQueryData<GetStreamsResponse>(getStreamsQueryKey);
      queryClient.setQueryData(getStreamsQueryKey, {
        data:
          previousStreams?.data.map((stream) =>
            stream.uuid === streamId
              ? {
                  ...stream,
                  name: name ?? stream.name,
                  language_uuid: language_uuid ?? stream.language_uuid,
                  type: type ?? stream.type,
                }
              : stream,
          ) ?? [],
      });
      return { previousStreams };
    },

    onSuccess: (_data, variables) => {
      if (variables.type) {
        toast.success(t("editor-notif:changeVttTypeSuccess"));
      }

      if (variables.name) {
        toast.success(t("editor-notif:renameStreamSuccess"));
      }
      if (variables.language_uuid) {
        toast.success(t("editor-notif:changeStreamLanguageSuccess"));
      }
    },

    onError: (
      _error: unknown,
      variables: UpdateStreamVariables,
      context: UpdateStreamContext | undefined,
    ): void => {
      if (variables.name) {
        toast.error(t("editor-notif:renameStreamError"), {
          duration: 3000,
        });
      }
      if (variables.language_uuid) {
        toast.error(t("editor-notif:changeStreamLanguageError"), {
          duration: 3000,
        });
      }
      queryClient.setQueryData(getStreamsQueryKey, context?.previousStreams);
    },
  };
}

interface StartEncodingVariables {
  profileUuid: string;
}

export function startEncodingMutation({
  mediaId,
  queryClient,
}: StreamMutationOptions): UseMutationOptions<
  StartPivotEncodingResponse,
  unknown,
  StartEncodingVariables
> {
  const getStreamsQueryKey = EncoderAPI.generateQuery({
    fn: EncoderAPI.getStreams,
    params: [mediaId],
  }).queryKey;

  const getFastAssetsQueryKey = MediaAPI.generateQuery({
    fn: MediaAPI.getMediaFastAssets,
    params: [mediaId],
  }).queryKey;

  return {
    mutationFn: async ({ profileUuid }: StartEncodingVariables) =>
      EncoderAPI.startPivotEncoding({ mediaId, profileUuid }),

    onMutate: () => {
      toast.info(t("editor-notif:encodingStarting"));
    },
    onSuccess: (): void => {
      queryClient.invalidateQueries(getStreamsQueryKey);
      queryClient.invalidateQueries(getFastAssetsQueryKey);
    },
  };
}
